import Transport from '@ledgerhq/hw-transport-webhid';
import {
  AddressVersion,
  createMessageSignature,
  deserializeTransaction,
  MessageSignature,
  MultiSigSpendingCondition,
  StacksTransaction,
} from '@stacks/transactions';
import StacksApp, { LedgerError, ResponseSign, ResponseVersion } from '@zondax/ledger-stacks';
import { compare } from 'compare-versions';
import { NETWORK } from 'constants/base';

import {
  BaseLedgerOperationContext,
  SemVerObject,
  versionObjectToVersionString,
} from './generic-ledger-utils';

export function requestPublicKeyForStxAccount(
  app: StacksApp,
  customPath: string,
  network: NETWORK
) {
  return async (index: number) =>
    app.getAddressAndPubKey(
      `${customPath}/${index}`,
      // We pass mainnet as it expects something, however this is so it can return a formatted address
      // We only need the public key, and can derive the address later in any network format
      network === 'mainnet' ? AddressVersion.MainnetSingleSig : AddressVersion.TestnetSingleSig
    );
}

/*interface StacksAppKeysResponseItem {
  path: string;
  stxPublicKey: string;
}*/

const LEDGER_VENDOR_ID = 0x2c97;
let connectPromise: Promise<StacksApp> | null = null;

async function closePreviouslyOpenedLedgerHidDevices() {
  const hid = typeof navigator === 'undefined' ? undefined : (navigator as any).hid;
  if (!hid?.getDevices) return;
  const devices = await hid.getDevices();
  const ledgerDevices = devices.filter(
    (device: any) => device.opened && device.vendorId === LEDGER_VENDOR_ID
  );
  await Promise.all(
    ledgerDevices.map(async (device: any) => {
      try {
        await device.close();
      } catch {
        // ignore close errors
      }
    })
  );
}

export async function connectLedgerStacksApp() {
  if (connectPromise) return connectPromise;
  connectPromise = (async () => {
    await closePreviouslyOpenedLedgerHidDevices();
    const transport = await Transport.create();

    return new StacksApp(transport as any);
  })();
  try {
    return await connectPromise;
  } finally {
    connectPromise = null;
  }
}

export async function getStacksAppVersion(app: StacksApp) {
  const resp = await app.getVersion();
  if (resp.errorMessage !== 'No errors') {
    throw new Error(resp.errorMessage);
  }

  return { name: 'Stacks', ...resp };
}

export function signLedgerTransaction(app: StacksApp) {
  return async (payload: Buffer, derivation: string) => app.sign(derivation, payload);
}

export function signLedgerUtf8Message(app: StacksApp) {
  return async (payload: string, path: string): Promise<ResponseSign> =>
    app.sign_msg(path, payload);
}

/*function signLedgerStructuredMessage(app: StacksApp) {
  return async (domain: string, payload: string, accountIndex: number): Promise<ResponseSign> =>
    app.sign_structured_msg(getStxDerivationPath(accountIndex), domain, payload);
}*/

export function signTransactionWithSignature(transaction: string, signatureVRS: Buffer) {
  const deserialzedTx = deserializeTransaction(transaction);
  const spendingCondition = createMessageSignature(signatureVRS.toString('hex'));
  const fields = (deserialzedTx.auth.spendingCondition as MultiSigSpendingCondition).fields;
  (fields[fields.length - 1].contents as MessageSignature) = spendingCondition;

  return deserialzedTx;
}

export function isStacksLedgerAppClosed(response: ResponseVersion) {
  const anotherUnknownErrorCodeMeaningAppClosed = 28161;

  return (
    response.returnCode === LedgerError.AppDoesNotSeemToBeOpen ||
    response.returnCode === anotherUnknownErrorCodeMeaningAppClosed
  );
}

// https://github.com/Zondax/ledger-stacks/issues/119
// https://github.com/hirosystems/wallet/issues/2567
const versionFromWhichContractPrincipalBugIsFixed = '0.23.3';

export function isVersionOfLedgerStacksAppWithContractPrincipalBug(
  currentDeviceVersion: SemVerObject
) {
  return compare(
    versionObjectToVersionString(currentDeviceVersion),
    versionFromWhichContractPrincipalBugIsFixed,
    '<'
  );
}

export interface LedgerTxSigningContext extends BaseLedgerOperationContext {
  transaction: StacksTransaction | null;
  signTransaction(): Promise<void> | void;
}
